import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { IFlight, ISearchForm, ISearchRequest } from "redux/searchForm";
import {
  dateObjToDateStringDotted,
  dateStringToDateObj,
  ICabinType,
  IItinerary,
} from "@thekilroygroup/shared-ui";
import {
  getMetaSearchQuery,
  isOutdatedMetaSearchQuery,
  isValidMetaSearchQuery,
} from "../../validation/validateMetaRequest";
import { IAppConfigObject } from "appConfig.interfaces";
import DOMPurify from "dompurify";
import { hasStudentOrYouth } from "utils/itineraryTagsUtils";

interface IArg {
  searchForm: ISearchForm;
  config: IAppConfigObject;
}
export const flightSearchApi: any = createApi({
  reducerPath: "flightSearchApi",
  baseQuery: fetchBaseQuery({
    method: "POST",
    headers: {
      Accept: "application/json, text/plain",
      "Content-Type": "application/json;charset=UTF",
      "Access-Control-Allow-Credentials": "true",
      "Access-Control-Allow-Methods": "POST, GET",
      "Access-Control-Allow-Origin": "*",
    },
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    fetchFlights: builder.query({
      query: (arg: IArg) => {
        const requestBody = getRequestBody(arg);
        return {
          url: `${arg.config.FLIGHT_SEARCH_API_BASE_URL}/search`,
          method: "POST",
          body: JSON.parse(DOMPurify.sanitize(JSON.stringify(requestBody))),
          selectFromResult: ({ response }: any) => response,
        };
      },
      transformResponse(apiResponse, meta) {
        return { apiResponse, meta };
      },
    }),
    fetchFlightsRedirect: builder.query({
      query: (arg: IArg) => {
        const requestBody = getRequestBody(arg);

        return {
          url: `${arg.config.FLIGHT_SEARCH_API_BASE_URL}/search-redirect`,
          method: "POST",
          body: JSON.parse(DOMPurify.sanitize(JSON.stringify(requestBody))),
        };
      },
      transformResponse(apiResponse, meta) {
        return { apiResponse, meta };
      },
    }),
    fetchFlightsForMonth: builder.query<IItinerary[], IArg>({
      // @ts-ignore
      async queryFn(arg, _queryApi, _extra, fetchWithBQ) {
        try {
          const searchDate = dateStringToDateObj(
            arg.searchForm.flights[0].departureDate as string,
          );

          // 1. Generate all anchor dates to fetch for the month
          const fetchDates = getSearchDatesForMonth(searchDate as Date);
          // 2. Call API in parallel
          const responses = await Promise.all(
            fetchDates.map((date) => {
              const updatedSearchForm = {
                ...arg.searchForm,
                flexibleSearch: 3,
                flights: [
                  {
                    ...arg.searchForm.flights[0],
                    departureDate: dateObjToDateStringDotted(date),
                  },
                ],
              };

              const body = JSON.parse(
                DOMPurify.sanitize(
                  JSON.stringify(
                    getRequestBody({
                      ...arg,
                      searchForm: {
                        ...updatedSearchForm,
                        flights: updatedSearchForm.flights.map((flight) => ({
                          ...flight,
                          departureDate:
                            flight.departureDate &&
                            typeof flight.departureDate === "object" &&
                            "toISOString" in flight.departureDate
                              ? (flight.departureDate as Date)
                                  .toISOString()
                                  .split("T")[0]
                              : (flight.departureDate as string),
                        })),
                      },
                    }),
                  ),
                ),
              );

              return fetchWithBQ({
                url: `${arg.config.FLIGHT_SEARCH_API_BASE_URL}/search`,
                method: "POST",
                body,
              });
            }),
          );

          // 3. Check for errors in any response
          const firstError = responses.find((res: any) => res.error);
          if (firstError) {
            return {
              error: {
                status: firstError.error?.status || "FETCH_ERROR",
                data: firstError.error?.data || firstError.error,
              },
            };
          }

          // 3. Flatten itineraries from all responses
          const allItineraries: IItinerary[] = [];
          responses.forEach((res: any) => {
            if (res.error) {
              console.error("Error in response:", res.error);
              return;
            }
            if (res.data?.itineraries) {
              // Filter itineraries to only include those from the searched month
              const monthItineraries = res.data.itineraries.filter(
                (itinerary: IItinerary) => {
                  if (!itinerary.flights?.length) return false;
                  const itineraryDate = new Date(
                    itinerary.flights[0].departureDateTime,
                  );
                  const searchMonth = searchDate as Date;
                  return (
                    itineraryDate.getMonth() === searchMonth.getMonth() &&
                    itineraryDate.getFullYear() === searchMonth.getFullYear()
                  );
                },
              );
              allItineraries.push(...monthItineraries);
            }
          });

          // 4. Pick cheapest regular AND cheapest student/youth itinerary per date
          const cheapestByDate: Record<
            string,
            { regular?: IItinerary; studentYouth?: IItinerary }
          > = {};

          allItineraries.forEach((itinerary) => {
            if (!itinerary.flights?.length) return;
            const date = itinerary.flights[0].departureDateTime.split("T")[0];
            const price = itinerary.totalFareAmounts.total;

            // Initialize date entry if it doesn't exist
            if (!cheapestByDate[date]) {
              cheapestByDate[date] = {};
            }

            if (hasStudentOrYouth(itinerary)) {
              // Handle student/youth itinerary
              if (
                !cheapestByDate[date].studentYouth ||
                price <
                  cheapestByDate[date].studentYouth!.totalFareAmounts.total
              ) {
                cheapestByDate[date].studentYouth = itinerary;
              }
            } else {
              // Handle regular itinerary
              if (
                !cheapestByDate[date].regular ||
                price < cheapestByDate[date].regular!.totalFareAmounts.total
              ) {
                cheapestByDate[date].regular = itinerary;
              }
            }
          });

          // 5. Flatten to include both regular and student/youth itineraries as separate entries
          const flattenedItineraries: Record<string, IItinerary> = {};

          Object.entries(cheapestByDate).forEach(([date, itineraries]) => {
            if (itineraries.regular) {
              flattenedItineraries[date] = itineraries.regular;
            }
            if (itineraries.studentYouth) {
              // Use a unique key for student/youth itineraries
              flattenedItineraries[`${date}_STU_YTH`] =
                itineraries.studentYouth;
            }
          });

          return { data: flattenedItineraries };
        } catch (error: any) {
          return {
            error: {
              status: "FETCH_ERROR",
              data: {
                message: error.message || "Failed to fetch flights for month",
              },
            },
          };
        }
      },
    }),
  }),
});

export const {
  useLazyFetchFlightsQuery,
  useFetchFlightsQuery,
  useLazyFetchFlightsRedirectQuery,
  useFetchFlightsRedirectQuery,
  useLazyFetchFlightsForMonthQuery,
  useFetchFlightsForMonthQuery,
} = flightSearchApi;

const getRequestBody = (arg: IArg) => {
  const basicSearchQuery: IBasicSearchRequest = getBasicSearchQuery(
    arg.searchForm,
  );

  const searchQuery: ISearchRequest = {
    ...basicSearchQuery,
    topLevelDomain: arg.config.TOP_LEVEL_DOMAIN as string,
  };

  if (
    isValidMetaSearchQuery(arg.searchForm) &&
    !isOutdatedMetaSearchQuery(arg.searchForm)
  ) {
    searchQuery["metaRedirectQuery"] = getMetaSearchQuery(arg.searchForm);
  }

  return searchQuery;
};

const getBasicSearchQuery = (searchForm: ISearchForm) => {
  const { airlines, cabinClass, flexibleSearch, flights, passengers } =
    searchForm;
  return {
    airlines: airlines,
    cabinClass: cabinClass || "ECONOMY",
    flexibleSearch: flexibleSearch,
    flights: flights,
    passengers: passengers,
  };
};

export interface IBasicSearchRequest {
  passengers: {
    adults: number;
    children: number;
    infants: number;
  };
  flexibleSearch: number;
  flights: IFlight[];
  cabinClass: ICabinType;
  airlines: string[] | null;
}
import {
  addDays,
  startOfMonth,
  isBefore,
  addMonths,
  format,
  startOfDay,
  isAfter,
  lastDayOfMonth,
} from "date-fns";

/**
 * Return an array of Date objects (search dates) with exactly 7 days spacing
 * that together (each covering ±3 days) cover the valid travel portion of the given month.
 *
 * @param {Date} inputDate - any date inside the month you want to cover
 * @returns {Date[]} array of search dates (ascending)
 */
export function getSearchDatesForMonth(inputDate: Date) {
  const today = startOfDay(new Date());

  const monthStart = startOfMonth(inputDate);
  const monthEnd = lastDayOfMonth(inputDate);

  // travel cannot start before today + 3
  const earliestAllowedTravel = addDays(today, 3);

  // if the first allowed travel day is after the month ends, nothing to search
  if (isAfter(earliestAllowedTravel, monthEnd)) return [];

  // the part of the month we must cover starts at the later of monthStart and earliestAllowedTravel
  const coverageStart = isAfter(monthStart, earliestAllowedTravel)
    ? monthStart
    : earliestAllowedTravel;

  // ideal first search date is coverageStart + 3 (so the first window begins on coverageStart)
  let firstSearch = addDays(coverageStart, 3);

  // but we must not return searches earlier than today + 6
  const earliestSearch = addDays(today, 6);
  if (isAfter(earliestSearch, firstSearch)) {
    firstSearch = earliestSearch;
  }

  // build the minimal sequence s, s+7, s+14, ... until we cover monthEnd
  const results = [];
  let s = firstSearch;

  // push all s while s + 3 < monthEnd (i.e. there are still month days to cover after this window)
  while (isBefore(addDays(s, 3), monthEnd)) {
    results.push(s);
    s = addDays(s, 7);
  }

  // push the final s that reaches or passes monthEnd
  results.push(s);

  return results;
}
